import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Plus, Calendar, User, Edit, Loader2, CheckSquare } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UnifiedTodoModal } from '@/components/crm/UnifiedTodoModal';
import { getPriorityColor, getStatusBadge, isTodoBlocked, UnifiedTodo } from '@/lib/todoHelpers';

interface OpportunityTodosProps {
  opportunityId: string;
}

export function OpportunityTodos({ opportunityId }: OpportunityTodosProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTodo, setSelectedTodo] = useState<UnifiedTodo | null>(null);

  const { data: todos = [], isLoading } = useQuery({
    queryKey: ['opportunity-todos', opportunityId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('unified_todos' as any)
        .select(`
          *,
          profiles:assigned_to(id, display_name, email),
          parent_todo:depends_on_todo_id(id, title, status)
        `)
        .eq('opportunity_id', opportunityId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as any as UnifiedTodo[];
    },
    retry: 1,
  });

  const toggleTodo = async (todoId: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'completed' ? 'open' : 'completed';
      const updateData: any = { 
        status: newStatus,
        completed_at: newStatus === 'completed' ? new Date().toISOString() : null
      };

      const { error } = await supabase
        .from('unified_todos' as any)
        .update(updateData)
        .eq('id', todoId);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['opportunity-todos', opportunityId] });
      queryClient.invalidateQueries({ queryKey: ['unified-todos'] });

      toast({
        title: `Taak ${newStatus === 'completed' ? 'afgerond' : 'heropend'}`,
      });
    } catch (error: any) {
      console.error('Error updating todo:', error);
      toast({
        title: 'Fout bij bijwerken',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const pendingTodos = todos.filter(t => t.status !== 'completed');
  const completedTodos = todos.filter(t => t.status === 'completed');

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <CheckSquare className="h-5 w-5" />
          To-do's
        </CardTitle>
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{pendingTodos.length} open</Badge>
          <Button size="sm" onClick={() => {
            setSelectedTodo(null);
            setIsModalOpen(true);
          }}>
            <Plus className="h-4 w-4 mr-1" />
            Nieuwe To-do
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : todos.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <CheckSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>Nog geen to-do's</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => {
              setSelectedTodo(null);
              setIsModalOpen(true);
            }}>
              <Plus className="h-4 w-4 mr-1" />
              Eerste to-do toevoegen
            </Button>
          </div>
        ) : (
          <>
            {/* Pending todos */}
            {pendingTodos.map((todo: any) => {
              const isBlocked = isTodoBlocked(todo, todos);
              const statusBadge = getStatusBadge(isBlocked ? 'blocked' : todo.status);
              
              return (
                <div key={todo.id} className="flex items-start gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                  <Checkbox
                    checked={todo.status === 'completed'}
                    onCheckedChange={() => toggleTodo(todo.id, todo.status)}
                    className="mt-1"
                    disabled={isBlocked}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-medium text-sm">{todo.title}</h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => {
                          setSelectedTodo(todo);
                          setIsModalOpen(true);
                        }}
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    {todo.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{todo.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Badge className={`text-xs ${getPriorityColor(todo.priority)}`}>
                        {todo.priority}
                      </Badge>
                      <Badge variant={statusBadge.variant} className="text-xs">
                        {statusBadge.label}
                      </Badge>
                      {todo.due_date && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(todo.due_date), 'dd MMM', { locale: nl })}
                        </div>
                      )}
                      {todo.profiles && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <User className="h-3 w-3" />
                          {todo.profiles.display_name || todo.profiles.email}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Completed todos (collapsed) */}
            {completedTodos.length > 0 && (
              <div className="pt-2 border-t">
                <p className="text-sm text-muted-foreground mb-2">{completedTodos.length} afgerond</p>
                {completedTodos.slice(0, 3).map((todo: any) => (
                  <div key={todo.id} className="flex items-center gap-3 p-2 opacity-60">
                    <Checkbox
                      checked={true}
                      onCheckedChange={() => toggleTodo(todo.id, todo.status)}
                    />
                    <span className="text-sm line-through">{todo.title}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>

      <UnifiedTodoModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTodo(null);
        }}
        todo={selectedTodo}
        onSave={async () => {
          await queryClient.invalidateQueries({ queryKey: ['opportunity-todos', opportunityId] });
          await queryClient.invalidateQueries({ queryKey: ['unified-todos'] });
        }}
        defaultOpportunityId={opportunityId}
      />
    </Card>
  );
}
